'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DataTable } from '@/components/data-table/data-table';
import { useDataTable } from '@/hooks/use-data-table';
import { useOrgNavigate } from '@/hooks/use-app-navigate';

import { getProcessorsTableColumns } from './processors-table-columns';
import { ProcessorsTableActionBar } from './processors-table-action-bar';
import { ProcessorsTableSkeleton } from './ProcessorsTableSkeleton';
import { BulkDeleteProcessorsModal } from './bulk-delete-processors-modal';
import { MainTableState } from '@/lib/utils/table-state-utils';
import { Processor } from '@/lib/api/processors';
import type { TableConfig } from '@/types/data-table';
import { DEFAULT_SKELETON_ROWS } from '@/config/data-table';

interface ProcessorsTableProps {
    processors: Processor[]
    total: number
    loading: boolean
    onTableStateChange: (state: MainTableState) => void
    onRefetch?: () => void
    config?: TableConfig
}

/**
 * ProcessorsTable component for displaying processors with DataTable functionality
 */
export function ProcessorsTable({
    processors,
    total,
    loading,
    onTableStateChange,
    onRefetch,
    config = {},
}: ProcessorsTableProps) {
    const { t, i18n } = useTranslation('processors');
    const navigate = useNavigate();
    const { orgRoutes } = useOrgNavigate();
    
    // State for single row delete
    const [processorToDelete, setProcessorToDelete] = React.useState<string | null>(null);

    const handleRowClick = React.useCallback(
        (processor: Processor) => {
            navigate(orgRoutes.PROCESSOR_DETAILS(processor.id));
        },
        [navigate, orgRoutes],
    );

    const handleDeleteProcessor = React.useCallback((processorId: string) => {
        setProcessorToDelete(processorId);
    }, []);

    const columns = React.useMemo(
        () =>
            getProcessorsTableColumns({
                onEditProcessor: (processorId: string) => {
                    console.log('Edit processor:', processorId);
                },
                onDeleteProcessor: handleDeleteProcessor,
                t,
                orgRoutes,
            }),
        [t, orgRoutes, i18n.language, handleDeleteProcessor],
    );

    const { table } = useDataTable({
        data: processors,
        columns,
        total,
        config,
        onTableStateChange,
        getRowId: (row: Processor) => row.id,
    });

    return (
        <>
            <DataTable
                key={i18n.language}
                table={table}
                actionBar={<ProcessorsTableActionBar table={table} />}
                isLoading={loading}
                skeletonRows={<ProcessorsTableSkeleton rows={config.skeletonRows || DEFAULT_SKELETON_ROWS} />}
                onRefresh={onRefetch}
                onRowClick={handleRowClick}
            />
            
            {/* Single row delete modal */}
            {processorToDelete && (
                <BulkDeleteProcessorsModal
                    processorIds={[processorToDelete]}
                    onClose={() => setProcessorToDelete(null)}
                    onSuccess={() => setProcessorToDelete(null)}
                />
            )}
        </>
    );
}

